<template>
  <div
    v-if="secondaryNavTitle"
    aria-label="Secondary navigation menu for sub-pages"
    role="navigation"
    data-test-id="secondary-nav"
    class="relative h-screen shrink-0 overflow-hidden border-r border-solid border-surface-dark motion-safe:transition-[width] motion-safe:duration-150 motion-safe:ease-linear hidden md:block"
    :class="isVisible ? 'w-56 sm:w-48 md:w-56' : 'w-0 border-r-0'"
  >
    <div
      class="h-full w-56 sm:w-48 md:w-56 overflow-x-clip overflow-y-auto text-clip whitespace-nowrap bg-surface-light px-4 select-none text-secondary pt-12 sm:pt-6"
    >
      <!-- Secondary Navigation Title -->
      <div
        ref="secondaryNavTitle"
        class="border-b border-solid border-surface-dark mb-6 pb-6 flex items-center"
      >
        <span class="block text-xl text-primary font-bold">
          {{ secondaryNavTitle }}
        </span>
        <div class="ml-auto">
          <!-- Toggle Button - When Open (beside title) -->
          <SecondaryNavToggleButton />
        </div>
      </div>

      <!-- Navigation Groups Wrapper -->
      <div class="max-h-screen">
        <template
          v-for="(group, groupIndex) in navigation"
          :key="groupIndex"
        >
          <span
            v-if="group.title"
            class="flex flex-row items-center font-bold h-9 "
          >
            {{ group.title }}
          </span>
          <ul
            v-if="group.items"
            :data-group="group.title"
            class="block text-primary mb-4 delay-150 max-h-[500px] overflow-x-hidden"
          >
            <template
              v-for="(item, itemIndex) in group.items"
              :key="itemIndex"
            >
              <SecondaryNavItem
                :label="item.label"
                :is-active="isCurrentNavigationItem(item)"
                :action="item.action"
              />
            </template>
          </ul>
        </template>
      </div>

    <!-- TODO: Labels section disabled - will be re-enabled later -->
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import SecondaryNavToggleButton from './SecondaryNavToggleButton.vue'
import SecondaryNavItem from './SecondaryNavItem.vue'

const store = useStore()

interface NavigationItem {
  label: string
  path?: string
  route?: string
  filterValue?: string
  filterModule?: string
  filterStateKey?: string
  action: () => void
}

interface NavigationGroup {
  title: string | null
  collapsedItems?: number
  items: NavigationItem[]
}

// Props removed - component now solely driven by Vuex store

const secondaryNavTitle = computed(() =>
  store.getters['secondaryNavigationModule/secondaryNavigationTitle'] ?? '',
)

const navigation = computed(() =>
  store.getters['secondaryNavigationModule/navigationItems'] ?? [],
)

const isVisible = computed(() =>
  store.getters['secondaryNavigationModule/isSecondaryNavigationVisible'] ?? true,
)
const route = useRoute()

// Check if navigation item is current route or active filter
const isCurrentNavigationItem = (item: NavigationItem) => {
  // Filter-based items (e.g. events / campaigns type filter) resolve against
  // the store module + state key supplied by the navigation config.
  if (item.filterValue !== undefined && item.filterModule && item.filterStateKey) {
    return store.state[item.filterModule]?.[item.filterStateKey] === item.filterValue
  }

  // Path-based items — prefer explicit path over label lookup
  if (item.path !== undefined) {
    return route.path === item.path
  }

  // Legacy label map for items without explicit path
  const urlMap: Record<string, string> = {
    'Contacts': '/audience',
    'My tags': '/audience/tags',
    'Saved filters': '/audience/filters',
    'Data capture': '/campaigns',
    'Archived Events': '/events/archived',
  }

  return route.path === urlMap[item.label]
}

watch(isVisible, (val) => {
  document.body.classList.toggle('secondary-nav-open', val)
}, { immediate: true })

onMounted(() => {
  // Sync localStorage with store on mount
  try {
    const savedValue = localStorage.getItem('secondaryNavVisible')
    if (savedValue !== null) {
      const savedVisibility = JSON.parse(savedValue)
      if (typeof savedVisibility === 'boolean') {
        store.commit('secondaryNavigationModule/SET_IS_SECONDARY_NAVIGATION_VISIBLE', savedVisibility)
      }
    }
  }
  catch (e) {
    console.warn('Failed to load secondary nav state from localStorage:', e)
  }
})
</script>

<style lang="css" scoped>
/** TODO: use TWCSS for this  */
.border-surface-dark {
  border-color: rgba(231, 229, 228, 1);
}
</style>
